import {
  useRef,
  Fragment,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

import uuid4 from "uuid4";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import {
  Text,
  Group,
  Radio,
  Stack,
  Title,
  Button,
  Divider,
  Textarea,
  TextInput,
  SimpleGrid,
  ActionIcon,
  NumberInput,
  createStyles,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { RiDeleteBin2Fill } from "react-icons/ri";

import { RequiredLabel } from "@components/RequiredLabel/RequiredLabel";
import { PhoneNumberInput } from "@components/PhoneNumberInput/PhoneNumberInput";
import { SubmissionConditionsInput } from "@components/SubmissionConditionsInput/SubmissionConditionsInput";

import { Draft } from "@utils/Draft.util";
import { useLanguageCode } from "@hooks/useLanguageCode";
import { currentStepAtom } from "../atoms/currentStep.atom";
import { getDefaultPhoneCodeUtil } from "@utils/getDefaultPhoneCode.util";

import { IStepRef } from "@models/Step.model";
import { IDemoRegistrationForm } from "@models/DemoRegistrationForm.model";

const createVehicleDraft = (
  lc: string
): Draft<IDemoRegistrationForm["vehicles"][number]> => ({
  id: uuid4(),
  primaryVehicleRepresentative: {
    phone: { code: getDefaultPhoneCodeUtil(lc) },
  },
  passengerTransport: null,
  secondaryVehicleRepresentative: {
    phone: { code: getDefaultPhoneCodeUtil(lc) },
  },
  isTrailerPlateNumberIdentical: "yes",
});

const useStyles = createStyles((t) => ({
  grid: {
    gridTemplateColumns: "repeat(2, 1fr)",
    [`@media (max-width: ${t.breakpoints.xs}px)`]: {
      gridTemplateColumns: "1fr",
    },
  },
}));

export const Step2 = forwardRef<
  IStepRef<IDemoRegistrationForm>,
  {
    readonly?: boolean;
    isSubmitted?: boolean;
    isSavePending?: boolean;
    draft?: Draft<IDemoRegistrationForm>;
    onChange?: (change: Draft<IDemoRegistrationForm>) => void;
    onSubmit?: () => void;
  }
>(
  (
    {
      draft = {},
      readonly = false,
      isSubmitted = false,
      isSavePending = false,
      onChange = () => {},
      onSubmit = () => {},
    },
    ref
  ) => {
    const { classes } = useStyles();

    const { t } = useTranslation();
    const setCurrentStep = useSetAtom(currentStepAtom);
    const { group, vehicles, conditionsAccepted } = draft;

    const lc = useLanguageCode();

    const form = useForm({
      initialValues: {
        conditionsAccepted,
        group: group || {},
        vehicles: vehicles || [],
        registerAnotherVehicle: "no",
      },
    });

    const $toRefs = { onChange, form, lc };
    const toRefs = useRef($toRefs);
    useEffect(() => {
      toRefs.current = $toRefs;
    });

    useEffect(() => {
      toRefs.current.onChange(form.values);
    }, [form.values]);

    useEffect(() => {
      if (form.values.registerAnotherVehicle === "yes") {
        toRefs.current.form.setFieldValue("registerAnotherVehicle", "no");
        toRefs.current.form.setFieldValue("vehicles", [
          ...toRefs.current.form.values.vehicles,
          createVehicleDraft(toRefs.current.lc),
        ]);
      }
    }, [form.values.registerAnotherVehicle]);

    useImperativeHandle(ref, () => ({
      getValues: () => form.values,
      isDirty: () => form.isDirty(),
      resetDirty: () => form.resetDirty(),
    }));

    return (
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack spacing="lg">
          <>
            <TextInput
              required
              label={t("Group name at the demonstration")}
              {...form.getInputProps("group.name")}
            />
            <Textarea
              required
              label={t(
                "Presentation of your group and interpretation of the motto"
              )}
              {...form.getInputProps("group.description")}
              maxLength={800}
              minRows={4}
            />
            <TextInput
              label={t("Other comments")}
              {...form.getInputProps("group.comments")}
            />
            {lc === "en" ? (
              <Group>
                <Text>We register a pedestrian group with approx.</Text>
                <NumberInput
                  required
                  hideControls
                  {...form.getInputProps("group.people")}
                />
              </Group>
            ) : (
              <Group>
                <Text>Wir melden eine Fussgruppe mit ca. </Text>
                <NumberInput
                  required
                  hideControls
                  {...form.getInputProps("group.people")}
                />
                <Text>Personen an</Text>
              </Group>
            )}
          </>
          {form.values.vehicles.map((vehicle, index, arr) => (
            <Fragment key={vehicle?.id || index}>
              <Divider />
              <Group spacing="xs">
                <Title order={5}>
                  {arr.length > 1 ? `${index + 1}.` : ""} {t("Vehicle")}
                </Title>
                <ActionIcon
                  color="red"
                  onClick={() => {
                    const value = [...form.values.vehicles];
                    value.splice(index, 1);
                    form.setFieldValue("vehicles", value);
                  }}
                >
                  <RiDeleteBin2Fill />
                </ActionIcon>
              </Group>
              <Radio.Group
                required
                spacing="xs"
                orientation="vertical"
                label={
                  <RequiredLabel
                    label="We register a vehicle in accordance with the terms and conditions of participation as follows:"
                    value={
                      form.values.vehicles[index]?.categories?.length
                        ? "filled"
                        : ""
                    }
                  />
                }
                {...form.getInputProps(`vehicles.${index}.categories`)}
              >
                <Radio
                  value="1"
                  label={t("Group category {{number}}", { number: 1 })}
                />
                <Radio
                  value="2"
                  label={t("Group category {{number}}", { number: 2 })}
                />
                <Radio
                  value="3"
                  label={t("Group category {{number}}", { number: 3 })}
                />
                <Radio
                  value="4"
                  label={t("Group category {{number}}", { number: 4 })}
                />
              </Radio.Group>
              <Radio.Group
                required
                label={
                  <RequiredLabel
                    label="Vehicle type"
                    value={form.values.vehicles[index]?.type || undefined}
                  />
                }
                {...form.getInputProps(`vehicles.${index}.type`)}
              >
                <Radio value="car" label={t("Passenger car")} />
                <Radio value="truckUpto7.5t" label={t("Truck upto 7.5t")} />
                <Radio value="truckOver7.5t" label={t("Truck over 7.5t")} />
                <Radio value="tractorWithTrailer" label={t("Semi-truck")} />
              </Radio.Group>
              <Radio.Group
                required
                label={
                  <RequiredLabel
                    label="Passenger transport on the loading area"
                    value={
                      form.values.vehicles[index]?.passengerTransport || ""
                    }
                  />
                }
                {...form.getInputProps(`vehicles.${index}.passengerTransport`)}
              >
                <Radio value="yes" label={t("Yes")} />
                <Radio value="no" label={t("No")} />
              </Radio.Group>
              <SimpleGrid className={classes.grid}>
                <TextInput
                  required
                  type="number"
                  label={t("Total length (in metres)")}
                  {...form.getInputProps(`vehicles.${index}.length`)}
                />
                <TextInput
                  required
                  type="number"
                  label={t("Total height (in metres)")}
                  {...form.getInputProps(`vehicles.${index}.height`)}
                />
                <TextInput
                  required
                  type="number"
                  label={t("Total width (in metres)")}
                  {...form.getInputProps(`vehicles.${index}.width`)}
                />
                <TextInput
                  required
                  type="number"
                  label={t("Total weight (in kg)")}
                  {...form.getInputProps(`vehicles.${index}.weight`)}
                />
              </SimpleGrid>
              <SimpleGrid className={classes.grid}>
                <TextInput
                  label={t("License plate number")}
                  {...form.getInputProps(`vehicles.${index}.plateNumber`)}
                />
                <Radio.Group
                  label={t("License plate number of tractor is identical.")}
                  {...form.getInputProps(
                    `vehicles.${index}.isTrailerPlateNumberIdentical`
                  )}
                >
                  <Radio value="yes" label={t("Yes")} />
                  <Radio value="no" label={t("No")} />
                </Radio.Group>
                {form.values.vehicles[index]?.isTrailerPlateNumberIdentical ===
                  "no" && (
                  <TextInput
                    required
                    label={t("Trailer Plate Number")}
                    style={{ gridColumn: "1 / -1" }}
                    {...form.getInputProps(
                      `vehicles.${index}.trailerPlateNumber`
                    )}
                  />
                )}
              </SimpleGrid>
              <Divider />
              <Title order={6}>{t("Primary Vehicle Representative")}</Title>
              <TextInput
                required
                label={t("Name")}
                {...form.getInputProps(
                  `vehicles.${index}.primaryVehicleRepresentative.name`
                )}
              />
              <SimpleGrid className={classes.grid}>
                <PhoneNumberInput
                  code={form.getInputProps(
                    `vehicles.${index}.primaryVehicleRepresentative.phone.code`
                  )}
                  number={form.getInputProps(
                    `vehicles.${index}.primaryVehicleRepresentative.phone.number`
                  )}
                />
                <TextInput
                  required
                  type="email"
                  label={t("Email")}
                  {...form.getInputProps(
                    `vehicles.${index}.primaryVehicleRepresentative.email`
                  )}
                />
              </SimpleGrid>
              <Title order={6}>{t("Secondary Vehicle Representative")}</Title>

              <TextInput
                label={t("Name")}
                {...form.getInputProps(
                  `vehicles.${index}.secondaryVehicleRepresentative.name`
                )}
              />
              <SimpleGrid className={classes.grid}>
                <PhoneNumberInput
                  required={false}
                  code={form.getInputProps(
                    `vehicles.${index}.secondaryVehicleRepresentative.phone.code`
                  )}
                  number={form.getInputProps(
                    `vehicles.${index}.secondaryVehicleRepresentative.phone.number`
                  )}
                />
                <TextInput
                  type="email"
                  label={t("Email")}
                  {...form.getInputProps(
                    `vehicles.${index}.secondaryVehicleRepresentative.email`
                  )}
                />
              </SimpleGrid>
            </Fragment>
          ))}
          <Radio.Group
            label={t(
              `forms.demo.labels.${
                form.values.vehicles.length === 0
                  ? "registerVehicle"
                  : "registerAnotherVehicle"
              }`
            )}
            {...form.getInputProps("registerAnotherVehicle")}
          >
            <Radio value="yes" label={t("Yes")} />
            <Radio value="no" label={t("No")} />
          </Radio.Group>
          <Divider />
          <SubmissionConditionsInput
            showTermsAndConditions={false}
            conditionsAccepted={form.getInputProps("conditionsAccepted")}
          />
          {!readonly && (
            <Group>
              <Button
                sx={{ flex: 1 }}
                variant="outline"
                onClick={() => setCurrentStep((pv) => pv - 1)}
              >
                {t("Back")}
              </Button>
              <Button type="submit" style={{ flex: 1 }} loading={isSavePending}>
                {isSavePending
                  ? `${t("Saving")}...`
                  : t(
                      isSubmitted
                        ? "forms.footer.update"
                        : "forms.footer.submit"
                    )}
              </Button>
            </Group>
          )}
        </Stack>
      </form>
    );
  }
);
